import { reactive } from 'vue'
import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import { API_HOST } from '@/api/request'

// 全局会话守卫：token 失效时弹重新登录框（而不是静默降级），
// 并兜底恢复受保护图片（/uploads 非公开目录）的加载凭证

export interface ReloginTenantOption {
  tenantId: number
  tenantName: string
}

export const reloginState = reactive({
  visible: false,
  loading: false,
  error: '',
  username: '',
  needTenant: false,
  tenants: [] as ReloginTenantOption[]
})

let pendingResolvers: ((ok: boolean) => void)[] = []

// 弹出全局重新登录框（单例：并发 401 共享同一弹窗，登录成功一起唤醒）
export function requireRelogin(): Promise<boolean> {
  if (!reloginState.visible) {
    const authStore = useAuthStore()
    reloginState.username = authStore.userInfo?.username || localStorage.getItem('lastUsername') || ''
    reloginState.error = ''
    reloginState.needTenant = false
    reloginState.tenants = []
    reloginState.visible = true
  }
  return new Promise(resolve => pendingResolvers.push(resolve))
}

// ReloginDialog 登录成功（ok=true）或用户选择退出登录（ok=false）后调用
export function resolveRelogin(ok: boolean) {
  reloginState.visible = false
  const rs = pendingResolvers
  pendingResolvers = []
  rs.forEach(r => r(ok))
}

// ============ 受保护图片加载失败恢复 ============
// <img> 请求不经 axios 拦截器，图片 401 历来静默失败（报销凭证图加载不出来的直接体感）。
// 这里全局捕获 IMG error：先用有效 access token 调 /auth/img-token 重发 cookie 再重载图片；
// access token 也失效则弹重新登录框，登录成功后重载。

const PUBLIC_IMAGE_RE = /\/images\/(products|common|signatures)\/|\/images\/t_\d+\/(products|common|signatures)\//
const MAX_IMG_RECOVER_ATTEMPTS = 2

const failedImages = new Map<string, { el: HTMLImageElement; attempts: number }>()
let recoverTimer: ReturnType<typeof setTimeout> | null = null
let recovering = false

const isProtectedUploadSrc = (src: string) =>
  src.includes('/uploads/images/') && !PUBLIC_IMAGE_RE.test(src)

export function installImageAuthGuard() {
  window.addEventListener(
    'error',
    (e) => {
      const el = e.target
      if (!(el instanceof HTMLImageElement)) return
      const src = el.getAttribute('src') || ''
      if (!isProtectedUploadSrc(src)) return
      const key = src.split('?')[0]
      const rec = failedImages.get(key)
      if (rec && rec.attempts >= MAX_IMG_RECOVER_ATTEMPTS) return
      failedImages.set(key, { el, attempts: (rec?.attempts || 0) + 1 })
      if (!recoverTimer) {
        recoverTimer = setTimeout(() => {
          recoverTimer = null
          void recoverImages()
        }, 600)
      }
    },
    true
  )
}

async function recoverImages() {
  if (recovering || failedImages.size === 0) return
  recovering = true
  try {
    const authStore = useAuthStore()
    if (!authStore.token) {
      if (await requireRelogin()) reloadFailedImages()
      return
    }
    try {
      // 独立 axios 实例：不套业务拦截器，避免 401 时与刷新/重登逻辑互相嵌套
      // （axios.defaults.baseURL 已在 request.ts 按平台设置为 '' 或 https://dqbotai.cn）
      await axios.post(`${API_HOST}/api/v1/auth/img-token`, null, {
        headers: { Authorization: `Bearer ${authStore.token}` },
        // 跨站 XHR 需带凭证,重发的 img_token Set-Cookie 才会被 WebView 存下(APP 场景)
        withCredentials: true
      })
      reloadFailedImages()
    } catch (e: any) {
      if (e?.response?.status === 401 && (await requireRelogin())) {
        reloadFailedImages()
      }
    }
  } finally {
    recovering = false
  }
}

function reloadFailedImages() {
  const entries = [...failedImages.values()]
  failedImages.clear()
  const bust = `_r=${Date.now()}`
  entries.forEach(({ el }) => {
    const base = (el.getAttribute('src') || '').split('?')[0]
    if (base) el.src = `${base}?${bust}`
  })
}
